import type { GISService, Layer } from '../../../utils/getLayerType.types';

enum LayerDisplayPriority {
  WMS_WMTS_LAYER = 0,
  BASEMAP_LAYER = 1,
  POLYGON_GEOMETRY = 2,
  POLYLINE_GEOMETRY = 3,
  OTHER_GEOMETRY = 4,
}

// Extended interfaces to include properties used in sorting
interface ServiceWithDisplayOrder extends GISService {
  display_order: number;
}

interface LayerWithGeometry extends Layer {
  geometryType: string;
}

interface CombinedLayer {
  layer: LayerWithGeometry;
  service: ServiceWithDisplayOrder;
}

/**
 * Calculates the layer display priority index for map layer ordering.
 * - display_order 0: WMS/WMTS layers (goes under basemap)
 * - display_order 1: Basemap layers (goes above WMS/WMTS)
 * - Other layers: Ordered by geometry type (polygon, polyline, etc.)
 * @param service - GIS service with display_order property
 * @param layerType - Layer geometry type (polygon, polyline, etc.)
 * @returns LayerDisplayPriority enum value for layer ordering
 */
const getLayerIndex = (
  service: ServiceWithDisplayOrder,
  layerType: string,
): LayerDisplayPriority => {
  if (service.display_order === 0) {
    return LayerDisplayPriority.WMS_WMTS_LAYER;
  } else if (service.display_order === 1) {
    return LayerDisplayPriority.BASEMAP_LAYER;
  } else {
    return layerType === 'polygon'
      ? LayerDisplayPriority.POLYGON_GEOMETRY
      : layerType === 'polyline'
        ? LayerDisplayPriority.POLYLINE_GEOMETRY
        : LayerDisplayPriority.OTHER_GEOMETRY;
  }
};

/**
 * Sorts combined GIS layers by display priority to ensure correct map layer ordering.
 * Prioritizes display_order 0 (WMS/WMTS) and 1 (basemap) layers, with WMS/WMTS rendered under basemap.
 * Other layers are ordered by geometry type. Layers are processed in ascending order for sequential map addition.
 * @param layers - Array of combined layer objects with service and layer properties
 * @returns Sorted array with WMS/WMTS first, then basemap, then other layers by geometry type
 */
export const sortLayersByDisplayPriority = (
  layers: CombinedLayer[],
): CombinedLayer[] => {
  return [...layers].sort((a, b) => {
    const layerIndexA = getLayerIndex(a.service, a.layer.geometryType);
    const layerIndexB = getLayerIndex(b.service, b.layer.geometryType);

    // Primary sort by layerIndex (ascending)
    if (layerIndexA !== layerIndexB) {
      return layerIndexA - layerIndexB;
    }

    // Secondary sort by service display_order for layers with same layerIndex
    return a.service.display_order - b.service.display_order;
  });
};
